'use client';

import { getCookie, setCookie } from '@/utils/cookies';
import { useCallback, useEffect, useState } from 'react';

export type ConsentLevel = 'none' | 'analytics' | 'full';

export const CONSENT_LEVELS: readonly ConsentLevel[] = ['none', 'analytics', 'full'];
export const CONSENT_DECISION_PENDING_KEY = 'lgpd_consent_decision_pending';

const CONSENT_COOKIE_NAME = 'lgpd_consent';
const CONSENT_COOKIE_MAX_AGE = 365; // days

export interface ConsentState {
  level: ConsentLevel;
  timestamp: number;
  version: string;
}

export function useConsentManagement() {
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedConsent = getCookie(CONSENT_COOKIE_NAME, true);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent) as ConsentState;
        setConsent(parsed);
      } catch {
        setConsent(null);
      }
    }
    setIsLoading(false);
  }, []);

  const updateConsent = useCallback((level: ConsentLevel) => {
    const newConsent: ConsentState = {
      level,
      timestamp: Date.now(),
      version: '1.0.0',
    };

    setCookie({
      name: CONSENT_COOKIE_NAME,
      value: JSON.stringify(newConsent),
      maxAge: CONSENT_COOKIE_MAX_AGE,
      path: '/',
      sameSite: 'lax',
      encode: true,
    });

    // The banner reloads the page right after deciding, so the consent event is emitted on the next load.
    try {
      sessionStorage.setItem(CONSENT_DECISION_PENDING_KEY, '1');
    } catch {
      // sessionStorage unavailable (private mode / blocked storage): the event is skipped, consent still applies.
    }

    setConsent(newConsent);
    return newConsent;
  }, []);

  const hasSessionReplayConsent = consent?.level === 'full';
  const hasAnalyticsConsent = consent?.level === 'analytics' || consent?.level === 'full';
  const needsConsent = !isLoading && consent === null;

  return {
    consent,
    isLoading,
    needsConsent,
    hasSessionReplayConsent,
    hasAnalyticsConsent,
    updateConsent,
  };
}
